import React from "react";
import { Snowflake, Camera, Activity } from "lucide-react";

const ITEMS = [
  {
    id: "frozen-hold",
    num: "01",
    icon: Snowflake,
    title: "Frozen Hold",
    body:
      "Your deposit isn't a charge — it's a pre-authorisation hold on your card. Watch the live countdown to auto-release the moment the trip ends.",
    chip: "Pre-auth · not a charge",
    visual: (
      <div className="flex items-center gap-3">
        <div className="relative w-2.5 h-2.5 rounded-full bg-carvny-red pulse-ring" />
        <div className="font-mono text-2xl font-bold text-carvny-ink tracking-tight tabular-nums">
          02:14:36
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-carvny-muted">
          until release
        </span>
      </div>
    ),
  },
  {
    id: "check-in",
    num: "02",
    icon: Camera,
    title: "360° Check-In",
    body:
      "Photograph all four sides of the car before you drive. Timestamped, GPS-stamped and stored — both sides can pull receipts in seconds.",
    chip: "Timestamped · stored",
    visual: (
      <div className="grid grid-cols-4 gap-1.5 w-full">
        {["Front", "Rear", "Left", "Right"].map((s) => (
          <div
            key={s}
            className="aspect-[5/3] rounded-md bg-white border border-carvny-border flex items-center justify-center"
          >
            <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-carvny-muted">
              {s}
            </span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "fine-tracking",
    num: "03",
    icon: Activity,
    title: "Live Fine Tracking",
    body:
      "RTA fines, Salik tolls and Dubai Police charges stream in real time — visible to both host and renter the moment they hit.",
    chip: "Real-time · no surprises",
    visual: (
      <div className="space-y-1.5 w-full">
        {[
          { label: "Salik · Al Maktoum", amt: "AED 4.00" },
          { label: "Salik · Al Garhoud", amt: "AED 4.00" },
          { label: "RTA · Speeding", amt: "AED 600.00" },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between text-[11px] sm:text-xs px-3 py-2 rounded-lg bg-white border border-carvny-border"
          >
            <span className="font-semibold text-carvny-ink">{row.label}</span>
            <span className="font-mono font-bold text-carvny-red tabular-nums">
              {row.amt}
            </span>
          </div>
        ))}
      </div>
    ),
  },
];

export default function SolutionSection() {
  return (
    <section
      id="solution"
      data-testid="solution-section"
      className="relative py-24 sm:py-32 lg:py-40 bg-carvny-surface border-t border-carvny-border"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Editorial header */}
        <div className="grid grid-cols-12 gap-y-8 lg:gap-x-12">
          <div className="col-span-12 lg:col-span-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-carvny-red">
              §02 — How CARVNY fixes it
            </div>
            <div className="rule mt-4" />
          </div>
          <div className="col-span-12 lg:col-span-8">
            <h2
              data-testid="solution-headline"
              className="font-display text-4xl sm:text-5xl lg:text-[64px] font-black tracking-tightest text-carvny-ink leading-[1.02]"
            >
              Three guardrails.
              <br className="hidden sm:block" />
              <span className="font-serif-italic font-normal text-carvny-ink/85"> Zero </span>
              <span className="text-carvny-red">guesswork.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-carvny-muted leading-relaxed max-w-2xl">
              We didn&apos;t reinvent rentals — we rebuilt the parts everyone
              keeps getting burned by. Every trip on CARVNY is wrapped in three
              layers of accountability.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
          {ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <article
                key={item.id}
                data-testid={`solution-card-${item.id}`}
                className="group relative bg-white border border-carvny-border rounded-[24px] p-8 sm:p-10 flex flex-col hover:border-carvny-red/60 hover:-translate-y-1 transition-all duration-300 shadow-[0_2px_0_rgba(0,0,0,0.02)] hover:shadow-[0_36px_64px_-32px_rgba(204,0,0,0.35)]"
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-carvny-red text-white flex items-center justify-center">
                    <Icon size={22} strokeWidth={2.2} />
                  </div>
                  <span className="font-display text-2xl font-black text-carvny-ink/15 tracking-tight">
                    {item.num}
                  </span>
                </div>

                <h3 className="font-display mt-10 text-[26px] sm:text-3xl font-bold tracking-tight text-carvny-ink leading-tight">
                  {item.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-carvny-muted">
                  {item.body}
                </p>

                {/* Visual mock */}
                <div className="mt-7 p-4 rounded-2xl bg-carvny-surface-2 border border-carvny-border">
                  {item.visual}
                </div>

                <div className="mt-8 pt-6 border-t border-carvny-border">
                  <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-carvny-red">
                    {item.chip}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
